// WorkspaceDock: a workspace's boards (`children`) with the dock beside them,
// or under them as the reader turned it: the chat, and what the board in
// front lends the dock (the cell picked on it: lib/dockBridge.js), or the
// cell picked on a board with no trace's own view behind it
// (lib/boardPickStore.js). The reading, the walkthrough and the cases
// touching the board are the chat's to show. Each place a workspace is drawn
// has one (`scope`): the workspace home's, beside its panes ("home"), and a
// workspace's over the page ("overlay"). They are the one dock: the same
// tabs, folded, turned and sized as the reader left the last.
//
// The app's one chat console docks in the dock in front. `chatFor`: the
// workspace whose chat it is on (useWorkspaceChat: null for none, undefined
// while one is on its way in); over the page, never the page's own. `behind`: the workspace over this dock while
// one is (a session previewed over the home's panes), else null: this dock
// answers nothing asked of the one in front, and when that one goes, the
// console docks here again, on the chat that was in front before, unless
// the workspace open here (`opened`, on its way in or not) is the one that
// was over it, which keeps its own.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NewChatButton, useWorkspaceChat } from "./CanvasChat.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import { BoardCellDock, BoardReadingHost } from "./TraceLanesView.jsx";
import { clearBoardPick, useBoardCard, useBoardPick } from "../lib/boardPickStore.js";
import { DockScope, closeDockTab, dockUp, pickDockTab, setDockSlot, useDockBridge } from "../lib/dockBridge.js";
import { useDockBodyHostRef } from "../lib/dockClaims.js";
import { getDockChatBar, setDockAvailable, setDockColumnExpanded, setDockHostPage } from "../lib/dockStore.js";

// The dock beside the boards or under them, and folded or not: the reader's
// choice wherever a workspace is (TraceLanesView keeps the same keys).
const DOCK_SIDE_KEY = "lanesDockSide";
const DOCK_MIN_KEY = "lanesDockMinimized";
const DOCK_SIDE_SHARE = 0.38;
// How much of the room the dock takes, beside the boards or under them, as
// the reader dragged it.
const DOCK_SHARE_KEY = "origin-workspace-home-dock-v1";
const DOCK_SHARE_MIN = 0.16;
const DOCK_SHARE_MAX = 0.72;

const readLocal = (key) => {
  try { return globalThis.localStorage?.getItem(key) ?? null; } catch { return null; }
};
const writeLocal = (key, value) => {
  try { globalThis.localStorage?.setItem(key, value); } catch { /* this tab only */ }
};
const readShares = () => {
  try { return JSON.parse(readLocal(DOCK_SHARE_KEY) || "null") || {}; } catch { return {}; }
};
const shareOf = (side) => {
  const x = Number(readShares()[side]);
  return Number.isFinite(x) && x >= DOCK_SHARE_MIN && x <= DOCK_SHARE_MAX ? x : DOCK_SIDE_SHARE;
};

export default function WorkspaceDock({ scope, chatFor, behind = null, opened = null, children, ...rest }) {
  const { ref, reclaim } = useDockBodyHostRef(true);
  useWorkspaceChat(chatFor, { apart: scope === "overlay" });
  useEffect(() => dockUp(scope), [scope]);
  const [beside, setBeside] = useState(() => readLocal(DOCK_SIDE_KEY) !== "0");
  const [folded, setFolded] = useState(() => readLocal(DOCK_MIN_KEY) === "1");
  // Beside the chat, what the board in front lends the dock, or the cell
  // picked on a board with no trace's own view. A cell picked brings the dock
  // open on it; closing it goes back to the chat.
  const bridge = useDockBridge(scope);
  const picked = useBoardPick(scope);
  // The trace last selected on the board in front, whose reading the chat
  // can run (read_trace_calls) while it is: held here with nothing to show.
  const chosen = useBoardCard(scope);
  const cardRead = chosen && !chosen.anchor && chosen.trace && chosen.turns?.length ? chosen : null;
  const lent = bridge.owner ? bridge.tabs : [];
  const [tab, setTab] = useState("chat");
  const unfold = () => {
    setFolded(false);
    writeLocal(DOCK_MIN_KEY, "0");
  };
  // A pane the board asks for comes in front.
  const seenAsk = useRef(bridge.ask);
  useEffect(() => {
    if (bridge.ask === seenAsk.current) return;
    seenAsk.current = bridge.ask;
    setTab("board");
    unfold();
  }, [bridge.ask]); // eslint-disable-line react-hooks/exhaustive-deps
  // The chat handed something from the workspace (lib/chatContextStore.js)
  // is brought in front, the dock unfolded, or a tab the board lent. Behind
  // another dock, that one answers instead.
  const seenTabAsk = useRef(bridge.tabAsk?.n ?? 0);
  useEffect(() => {
    const ask = bridge.tabAsk;
    if (!ask || ask.n === seenTabAsk.current) return;
    seenTabAsk.current = ask.n;
    if (behind) return;
    if (ask.tab === "chat") setTab("chat");
    else {
      setTab("board");
      pickDockTab(ask.tab, scope);
    }
    unfold();
  }, [bridge.tabAsk]); // eslint-disable-line react-hooks/exhaustive-deps
  const seenPick = useRef(picked?.ask ?? 0);
  useEffect(() => {
    if (!picked || picked.ask === seenPick.current) return;
    seenPick.current = picked.ask;
    setTab("pick");
    unfold();
  }, [picked]);
  const shownTab = tab === "pick" && picked ? "pick"
    : tab === "board" && bridge.owner && lent.some((t) => t.id === bridge.active) ? "board" : "chat";
  // A tab closed while it is in front gives the dock back to the chat; one
  // closed from behind leaves the dock where it is.
  const closeTab = (inFront, close) => {
    close();
    if (inFront) setTab("chat");
  };
  // Named for where its dock has stepped to, not only where it was clicked.
  const pickAt = picked?.at || picked;
  const pickLane = picked?.record?.model?.laneById?.get(pickAt?.laneId)?.label || null;
  const tabs = [
    { id: "chat", label: "Chat", active: shownTab === "chat", onSelect: () => setTab("chat") },
    ...lent.map((t) => ({
      id: `board:${t.id}`, label: t.label, dot: t.dot || null, active: shownTab === "board" && bridge.active === t.id,
      onSelect: () => { setTab("board"); pickDockTab(t.id, scope); },
      onClose: t.closable ? () => closeTab(shownTab === "board" && bridge.active === t.id, () => closeDockTab(t.id, scope)) : null,
    })),
    ...(picked ? [{
      id: "pick", label: pickLane ? `T${(picked.base || 0) + pickAt.turn} ${pickLane}` : `Turn ${(picked.base || 0) + pickAt.turn}`, active: shownTab === "pick",
      onSelect: () => setTab("pick"),
      onClose: () => closeTab(shownTab === "pick", () => clearBoardPick(null, scope)),
    }] : []),
  ];
  const [share, setShare] = useState(() => shareOf(beside ? "w" : "h"));
  const shareRef = useRef(share);
  shareRef.current = share;
  const rowRef = useRef(null);
  const seam = useRef(null);
  const slotRef = useCallback((el) => setDockSlot(el, scope), [scope]);
  useEffect(() => {
    setDockAvailable(true);
    setDockHostPage(true);
    return () => { setDockAvailable(false); setDockHostPage(false); setDockColumnExpanded(false); };
  }, []);
  useEffect(() => { setDockColumnExpanded(!folded); }, [folded]);
  // Behind another dock, the console docks in that one, on its workspace's
  // chat; that one put away, the console comes back here, on the chat that
  // was in front before. The workspace that was in front come here (mounted:
  // on its way to a pane), the chat it was worked in stays in front.
  const lastBehind = useRef(null);
  useEffect(() => { if (behind) lastBehind.current = behind; }, [behind]);
  const chatBefore = useRef(undefined);
  // The console comes back before the page is drawn again, in the frame the
  // one in front goes: never a frame without it, nor one with it undocked.
  useLayoutEffect(() => {
    if (!behind && chatBefore.current !== undefined) reclaim();
  }, [behind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (behind) {
      if (chatBefore.current === undefined) chatBefore.current = (getDockChatBar()?.tabs || []).find((t) => t.active)?.id ?? null;
      return;
    }
    if (chatBefore.current === undefined) return;
    const was = chatBefore.current;
    chatBefore.current = undefined;
    setDockAvailable(true);
    setDockHostPage(true);
    setDockColumnExpanded(!folded);
    if (opened && opened === lastBehind.current) return;
    const tab = was == null ? null : (getDockChatBar()?.tabs || []).find((t) => String(t.id) === String(was));
    if (tab && !tab.active) tab.onSelect?.();
  }, [behind]); // eslint-disable-line react-hooks/exhaustive-deps
  const turn = () => {
    const next = !beside;
    setBeside(next);
    setShare(shareOf(next ? "w" : "h"));
    writeLocal(DOCK_SIDE_KEY, next ? "1" : "0");
  };
  const fold = () => {
    const next = !folded;
    setFolded(next);
    writeLocal(DOCK_MIN_KEY, next ? "1" : "0");
  };
  // The seam: dragged, it shares the room between the boards and the dock.
  // While it is, the body says so (lib/settledResize.js), so what redraws
  // or measures itself at each size waits for the drag to end.
  const seamDown = (e) => {
    if (e.button !== 0 || !rowRef.current) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
    seam.current = rowRef.current.getBoundingClientRect();
    document.body.classList.add("is-resizing-dock");
  };
  const seamMove = (e) => {
    const r = seam.current;
    if (!r) return;
    // A drag whose release was missed (let go outside the window, the
    // pointer scrolled away) ends at the next move with no button held,
    // rather than the seam following the pointer from then on.
    if (!(e.buttons & 1)) {
      seamUp();
      return;
    }
    const x = beside ? (r.right - e.clientX) / r.width : (r.bottom - e.clientY) / r.height;
    setShare(Math.max(DOCK_SHARE_MIN, Math.min(DOCK_SHARE_MAX, x)));
  };
  const seamUp = () => {
    if (!seam.current) return;
    seam.current = null;
    document.body.classList.remove("is-resizing-dock");
    writeLocal(DOCK_SHARE_KEY, JSON.stringify({ ...readShares(), [beside ? "w" : "h"]: shareRef.current }));
  };
  // A dock taken away mid-drag leaves no drag behind.
  useEffect(() => () => {
    if (seam.current) document.body.classList.remove("is-resizing-dock");
  }, []);
  const rail = beside && folded;
  // Its arrow points the way the dock folds: aside beside the boards, down
  // under them.
  const foldArrow = beside ? (folded ? "M7 3L4 6l3 3" : "M5 3l3 3-3 3") : (folded ? "M3 7l3-3 3 3" : "M3 5l3 3 3-3");
  const control = `flex cursor-pointer items-center justify-center text-[color:var(--muted)] hover:text-[color:var(--ink)] ${rail ? "h-[26px] w-full" : "h-full w-[26px]"}`;
  const controls = (
    <>
      <HoverTooltip placement={rail ? "left" : "top"} label={folded ? "Show the dock" : beside ? "Fold the dock to the side" : "Minimize the dock to its tabs"}>
        <button type="button" data-dock-minimize="" aria-label={folded ? "Restore the dock" : "Minimize the dock"} aria-expanded={!folded} onClick={fold} className={control}>
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={foldArrow} /></svg>
        </button>
      </HoverTooltip>
      <HoverTooltip placement={rail ? "left" : "top"} label={beside ? "Dock it under the board" : "Dock it beside the board"}>
        <button type="button" data-dock-turn="" aria-label={beside ? "Dock under the board" : "Dock beside the board"} onClick={turn} className={control}>
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" aria-hidden="true">
            <rect x="1.5" y="2" width="9" height="8" rx="1" />
            <path d={beside ? "M1.5 7h9" : "M7 2v8"} />
          </svg>
        </button>
      </HoverTooltip>
    </>
  );
  return (
    <DockScope.Provider value={scope}>
      <div ref={rowRef} className={`flex h-full min-h-0 min-w-0 ${beside ? "flex-row" : "flex-col"}`} data-workspace-dock={scope} {...rest}>
        <div className="min-h-0 min-w-0 flex-1">
          {children}
        </div>
        {folded ? (
          <div className={beside ? "w-[10px] flex-none" : "h-[10px] flex-none"} aria-hidden="true" />
        ) : (
          <div
            role="separator"
            aria-orientation={beside ? "vertical" : "horizontal"}
            aria-label="Resize the dock"
            data-dock-divider=""
            onPointerDown={seamDown}
            onPointerMove={seamMove}
            onPointerUp={seamUp}
            onPointerCancel={seamUp}
            onLostPointerCapture={seamUp}
            className={`group flex flex-none select-none items-center justify-center ${beside ? "w-[10px] cursor-col-resize" : "h-[10px] cursor-row-resize"}`}
            style={{ touchAction: "none" }}
          >
            {/* The app's column knob: shown pointed at or dragged, not at rest. */}
            <span className={`rounded-full bg-[color:var(--line-strong)] opacity-0 transition-opacity duration-[180ms] group-hover:opacity-100 ${beside ? "h-[48px] w-[3px]" : "h-[3px] w-[48px]"}`} />
          </div>
        )}
        <div
          className={`flex min-h-0 min-w-0 flex-none flex-col rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] ${rail ? "w-[30px] items-center pt-[2px]" : ""}`}
          style={folded ? undefined : beside ? { width: `${(share * 100).toFixed(2)}%` } : { height: `${(share * 100).toFixed(2)}%` }}
          data-lanes-dock-shell=""
          data-dock-minimized={folded ? "" : undefined}
          data-dock-side={beside ? "" : undefined}
        >
          {rail ? (
            <div className="flex w-full flex-col items-center gap-[2px]" data-dock-rail="">{controls}</div>
          ) : (
            <div role="tablist" aria-label="Detail dock" className={`flex h-[37px] min-w-0 flex-none items-stretch ${folded ? "" : "border-b border-[color:var(--line-soft)]"}`}>
              {/* Words muted until pointed at, a hairline between, the one in
                  front lit, a picked cell's with its close. Inside the dock's
                  frame, its foot at a rail button's: one row with the
                  sidebar's search and the board's bar. */}
              {tabs.map((t) => (
                <div
                  key={t.id}
                  data-dock-tab={t.id}
                  className={`flex min-w-0 max-w-[320px] items-stretch border-r border-[color:var(--line-soft)] ${t.active ? "[background-image:linear-gradient(var(--green-soft),var(--green-soft))]" : ""}`}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={t.active}
                    onClick={t.onSelect}
                    className={`flex min-w-0 items-center gap-[6px] pl-[9px] text-[11px] ${t.onClose ? "pr-[3px]" : "pr-[9px]"} ${t.active ? "text-[color:var(--ink)]" : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
                  >
                    {t.dot ? <span aria-hidden="true" className={`h-[6px] w-[6px] flex-none rounded-full ${t.dot}`} /> : null}
                    <span className="min-w-0 truncate leading-[16px]">{t.label}</span>
                  </button>
                  {t.onClose ? (
                    <button
                      type="button"
                      aria-label={`Close ${t.label}`}
                      onClick={t.onClose}
                      className="flex w-[20px] flex-none cursor-pointer items-center justify-center text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                    >
                      &times;
                    </button>
                  ) : null}
                </div>
              ))}
              <div className="ml-auto flex flex-none items-stretch">
                {shownTab === "chat" ? <NewChatButton className={control} /> : null}
                {controls}
              </div>
            </div>
          )}
          <div ref={ref} className={`min-h-0 min-w-0 flex-1 bg-[color:var(--bg)] ${folded || shownTab !== "chat" ? "hidden" : ""}`} data-workspace-chat="" data-dock-framed="" />
          {/* Where the board in front draws the panes of the tabs it lends. */}
          <div ref={slotRef} className={!folded && shownTab === "board" ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"} data-dock-board-pane="" />
          {/* Kept while its trace is the one selected, so its reading, run
              from the chat, goes on as the reader moves about. */}
          {cardRead ? <BoardReadingHost key={`${cardRead.board}:${cardRead.session}`} card={cardRead} /> : null}
          {!folded && shownTab === "pick" ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-dock-pick-pane="">
              <BoardCellDock pick={picked} onClose={() => { clearBoardPick(null, scope); setTab("chat"); }} />
            </div>
          ) : null}
        </div>
      </div>
    </DockScope.Provider>
  );
}
